/**
 * EL ESTADO DEL MODO SIN CONEXIÓN, en un solo sitio y sin React.
 *
 * Tres cosas que leen a la vez la red (`redOffline`), la caché de consultas
 * (`persistidorOffline`) y el aviso de la pantalla (`AvisoSinConexion`):
 *
 *   · DE QUIÉN se guarda: el usuario con sesión. Lo fija `AuthContext` en cuanto
 *     lo sabe y lo quita al cerrar sesión. Sin usuario no se guarda ni se lee
 *     nada: una pantalla pública nunca ve lo de nadie.
 *   · SI HAY RED, según el sistema (`navigator.onLine` y sus eventos).
 *   · SI LO QUE SE VE VIENE DE LO GUARDADO: la red falló o tardó y se enseñó la
 *     última copia. Se guarda la hora de esa copia para poder decir «datos de
 *     las 10:32», y se limpia en cuanto una lectura vuelve a llegar de verdad.
 */

export interface EstadoOffline {
  /**
   * El interruptor `platform_settings.modo_sin_conexion` para la empresa de este
   * usuario (ver `interruptorOffline`). Apagado, NADA del modo sin conexión hace
   * nada: la app se comporta exactamente como antes.
   */
  activo: boolean;
  uid: string | null;
  /** Lo que dice el sistema. `true` no garantiza red útil (cobertura de invernadero). */
  enLinea: boolean;
  /** La pantalla está enseñando una copia guardada. */
  mostrandoGuardado: boolean;
  /** Hora (ms) de la copia más antigua que se ha enseñado desde que se perdió la red. */
  guardadoDesde: number | null;
  /** Última vez (ms) que una lectura llegó del servidor. */
  ultimaRespuestaReal: number | null;
}

let estado: EstadoOffline = {
  activo: false,
  uid: null,
  enLinea: typeof navigator === "undefined" ? true : navigator.onLine !== false,
  mostrandoGuardado: false,
  guardadoDesde: null,
  ultimaRespuestaReal: null,
};

const oyentes = new Set<() => void>();

function cambiar(parcial: Partial<EstadoOffline>): void {
  const siguiente = { ...estado, ...parcial };
  const igual = (Object.keys(parcial) as (keyof EstadoOffline)[]).every((k) => siguiente[k] === estado[k]);
  if (igual) return;
  estado = siguiente;
  oyentes.forEach((f) => {
    try {
      f();
    } catch {
      /* un oyente roto no para a los demás */
    }
  });
}

export function leerEstadoOffline(): EstadoOffline {
  return estado;
}

export function suscribirEstadoOffline(f: () => void): () => void {
  oyentes.add(f);
  return () => oyentes.delete(f);
}

/* ─── Interruptor ─────────────────────────────────────────────────────── */

export function fijarModoActivo(activo: boolean): void {
  cambiar({ activo });
}

/** ¿Está encendido el modo sin conexión para este usuario y su empresa? */
export function modoActivo(): boolean {
  return estado.activo;
}

/* ─── Usuario ─────────────────────────────────────────────────────────── */

export function fijarUsuarioOffline(uid: string | null): void {
  cambiar({ uid });
}

export function usuarioOffline(): string | null {
  return estado.uid;
}

/* ─── Red ─────────────────────────────────────────────────────────────── */

/** `false` solo cuando el sistema dice con seguridad que no hay red. */
export function sinRedSegura(): boolean {
  try {
    return typeof navigator !== "undefined" && navigator.onLine === false;
  } catch {
    return false;
  }
}

/** La red falló o tardó y se enseñó la copia de `guardadaEn`. */
export function marcarServidoGuardado(guardadaEn: number): void {
  const desde = estado.guardadoDesde == null ? guardadaEn : Math.min(estado.guardadoDesde, guardadaEn);
  cambiar({ mostrandoGuardado: true, guardadoDesde: desde });
}

/** Una lectura llegó del servidor de verdad. */
export function marcarRespuestaReal(): void {
  const ahora = Date.now();
  // No se emite en cada respuesta: solo cuando cambia algo que se pinta.
  if (estado.mostrandoGuardado || estado.ultimaRespuestaReal == null || ahora - estado.ultimaRespuestaReal > 30_000) {
    cambiar({ ultimaRespuestaReal: ahora, mostrandoGuardado: false, guardadoDesde: null });
  }
}

/* ─── Red dudosa ──────────────────────────────────────────────────────────
   Cuándo merece la pena NO esperar a la red si ya hay una copia guardada: el
   sistema dice que no hay red, o hace poco que una lectura falló o tardó
   mucho. Con red buena se espera siempre a la respuesta de verdad, para no
   enseñar datos viejos sin necesidad. */

const VENTANA_RED_MALA_MS = 2 * 60 * 1000;
let ultimoTropiezo = 0;
const alTropezar = new Set<() => void>();

export function marcarTropiezoDeRed(): void {
  ultimoTropiezo = Date.now();
  alTropezar.forEach((f) => {
    try {
      f();
    } catch {
      /* nada */
    }
  });
}

/** Avisa cuando una petición a Supabase falla por red (para entrar con lo guardado sin esperar). */
export function alTropezarLaRed(f: () => void): () => void {
  alTropezar.add(f);
  return () => alTropezar.delete(f);
}

/**
 * ¿Se está enseñando lo guardado porque la red no contesta, y desde entonces no
 * ha llegado nada de verdad? Entonces cada consulta contesta YA con la copia
 * (la red sigue por detrás): esperar el margen en cada una sumaba segundos.
 */
export function enRafagaDeCopias(): boolean {
  return estado.mostrandoGuardado && !escrituraReciente();
}

/* ─── Escritura reciente ──────────────────────────────────────────────────
   Tras un cambio (crear, editar, borrar, completar…) NUNCA se contesta con lo
   guardado aunque la red vaya lenta: la lectura que sigue a una escritura tiene
   que traer lo que se acaba de escribir. Si no, la tarea recién creada «no
   aparece» y se crea dos veces (riesgo 1 del análisis del 11-10-2026). Con la
   red caída de verdad la lectura falla y entonces sí sale la copia, como siempre. */

export const VENTANA_TRAS_ESCRITURA_MS = 30_000;
let ultimaEscritura = 0;

export function marcarEscritura(): void {
  ultimaEscritura = Date.now();
}

export function escrituraReciente(): boolean {
  return Date.now() - ultimaEscritura < VENTANA_TRAS_ESCRITURA_MS;
}

/* ─── Lo que el usuario está cargando ─────────────────────────────────────
   Peticiones a Supabase de la propia app (no de la descarga) que siguen en vuelo.
   La descarga en segundo plano espera a que esto esté quieto: así no le quita
   red a lo que el usuario está mirando (riesgo 4). */

let enVuelo = 0;
export function empiezaPeticionDeUsuario(): void {
  enVuelo++;
}
export function acabaPeticionDeUsuario(): void {
  enVuelo = Math.max(0, enVuelo - 1);
  if (enVuelo === 0) ultimaQuietud = Date.now();
}
let ultimaQuietud = Date.now();

/** Espera a que no haya peticiones del usuario en vuelo durante `quieto` ms (con tope). */
export async function esperarAppQuieta(quieto = 1500, tope = 60_000): Promise<void> {
  const inicio = Date.now();
  while (Date.now() - inicio < tope) {
    if (enVuelo === 0 && Date.now() - ultimaQuietud >= quieto) return;
    await new Promise((r) => setTimeout(r, 250));
  }
}

/**
 * Hasta que llega la PRIMERA respuesta de verdad de este arranque, la red
 * también es dudosa: abrir la app en un invernadero con media raya es justo
 * cuando más se espera (renovar el token puede tardar decenas de segundos) y
 * cuando más falta hace enseñar ya lo guardado. Con red buena la primera
 * respuesta llega antes del margen y esto no cambia nada.
 */
export function redDudosa(): boolean {
  if (sinRedSegura()) return true;
  if (escrituraReciente()) return false;
  return estado.ultimaRespuestaReal === null || Date.now() - ultimoTropiezo < VENTANA_RED_MALA_MS;
}

/* ─── Eventos del sistema ─────────────────────────────────────────────── */

let escuchando = false;
const alVolverLaRed = new Set<() => void>();

/** Avisa cuando el sistema dice que vuelve a haber red (para refrescar lo visto). */
export function alRecuperarRed(f: () => void): () => void {
  alVolverLaRed.add(f);
  return () => alVolverLaRed.delete(f);
}

export function escucharRedDelSistema(): void {
  if (escuchando || typeof window === "undefined") return;
  escuchando = true;
  window.addEventListener("online", () => {
    cambiar({ enLinea: true });
    alVolverLaRed.forEach((f) => {
      try {
        f();
      } catch {
        /* nada */
      }
    });
  });
  window.addEventListener("offline", () => {
    cambiar({ enLinea: false });
    marcarTropiezoDeRed();
  });
}

/** Solo para las pruebas: vuelve al estado de arranque. */
export function __reiniciarEstadoOffline(): void {
  ultimaEscritura = 0;
  enVuelo = 0;
  estado = {
    activo: true,
    uid: null,
    enLinea: true,
    mostrandoGuardado: false,
    guardadoDesde: null,
    ultimaRespuestaReal: null,
  };
  ultimoTropiezo = 0;
  oyentes.clear();
  alVolverLaRed.clear();
  alTropezar.clear();
}
